import { withAnalytics } from 'contexts/Analytics/Analytics';
import { withApi } from 'contexts/Api/Api';
import { withContent } from 'contexts/Content/Content';
import { withPage } from 'contexts/Page/Page';
import { withSettings } from 'contexts/Settings/Settings';
import { withSounds } from 'contexts/Sounds/Sounds';
import { compose, getCards, getCardsSides, getCurrentPot, getExpectedPot, getExpectedValue, getTime, wait } from 'helpers';
import _ from 'lodash';
import { withRouter } from 'next/router';
import { Component, createContext, useContext } from 'react';
import * as setup from 'setup';

export const GameContext = createContext<GameState>({} as GameState);

export const useGame = () => useContext(GameContext);

class Game extends Component<GameProviderProps, GameState> {
    initialState = {
        actions: [],
        budget: 0,
        cardValues: [],
        communityCards: [],
        communityCardsSides: [],
        currentPot: 0,
        earnings: 0,
        expectedPot: 0,
        expectedValue: 0,
        hands: 0,
        holeCards: [],
        holeCardsSide: null,
        holeHandName: null,
        opponents: 1,
        opponentsCards: [],
        opponentsCardsSides: [],
        opponentsHandNames: [],
        opponentsSpied: [],
        remainingCosts: 0,
        resultType: null,
        round: 'hole' as GameRound,
        roundCost: 0,
        shouldPay: true,
        winningCards: null,
        winProbability: 0,
    };

    updateEvaluationTimeout: NodeJS.Timeout;

    constructor(props: GameProviderProps) {
        super(props);

        // Get history
        const time = getTime();
        const prevTime = parseInt(window.localStorage.getItem('sp.game.history.time') || `${time}`);
        const expired = !!setup.game.history.expiration && time - prevTime > setup.game.history.expiration;
        const history = JSON.parse((!expired && window.localStorage.getItem('sp.game.history')) || '{}');

        // Initialize state
        this.state = {
            ...this.initialState,
            initialized: !props.page.lesson,
            history,
            lesson: null,
            lessonProps: {},
            lessonReady: false,
            loading: false,
            ready: false,
            startingBudget: 0,
            allIn: this.allIn,
            continueAction: this.continueAction,
            fold: this.fold,
            getHistory: this.getHistory,
            interceptAction: this.interceptAction,
            pay: this.pay,
            reset: this.reset,
            resetLessonState: this.resetLessonState,
            rewind: this.rewind,
            setCardsSides: this.setCardsSides,
            setCommunityCards: this.setCommunityCards,
            setHands: this.setHands,
            setHoleCards: this.setHoleCards,
            setLessonProps: this.setLessonProps,
            setResultType: this.setResultType,
            showOpponents: this.showOpponents,
            shuffleDeal: this.shuffleDeal,
            spyOpponent: this.spyOpponent,
            storeHistory: this.storeHistory,
            updateEvaluation: this.updateEvaluation,
        };
    }

    componentDidUpdate(_prevProps: GameProviderProps, prevState: GameState) {
        const { page } = this.props;
        const {
            communityCards,
            communityCardsSides,
            holeCards,
            holeCardsSide,
            lesson,
            lessonProps,
            lessonReady,
            opponentsCardsSides,
            round,
            winProbability,
        } = this.state;

        // Update lesson
        if (page.lesson && page.lesson !== lesson) {
            this.updateLesson(page.lesson, lesson, page.interacted);
        }

        // Check if cards are updated
        const cardsUpdated =
            communityCards !== prevState.communityCards ||
            communityCardsSides !== prevState.communityCardsSides ||
            opponentsCardsSides !== prevState.opponentsCardsSides ||
            holeCards !== prevState.holeCards ||
            holeCardsSide !== prevState.holeCardsSide;

        // Update evaluation
        if (cardsUpdated && lesson && lessonReady && !lessonProps.skipUpdateEvaluation) {
            this.debounceUpdateEvaluation();
        }

        // Update round
        if (cardsUpdated || round !== prevState.round || winProbability !== prevState.winProbability) {
            this.updateRound();
        }
    }

    updateLesson = (lesson: string, prevLesson: string | null, interacted: boolean) => {
        const { api, settings } = this.props;
        const { history, ready } = this.state;
        const showGuestLessonShell = api.authConfigured && !api.user;

        // Check current lesson based on history
        if (!ready && !showGuestLessonShell) {
            const lessonIndex = setup.lessonKeys.indexOf(lesson);
            const lastLesson = Object.keys(history).pop()?.split('-')[0];
            const historyLastIndex = lastLesson ? setup.lessonKeys.indexOf(lastLesson) : 0;
            const resumeIndex = api.resumeLesson ? setup.lessonKeys.indexOf(api.resumeLesson) : -1;
            const lastIndex = Math.max(historyLastIndex, resumeIndex, 0);
            if (!settings.debugProgress && (lessonIndex === -1 || lessonIndex > lastIndex)) {
                const fallbackLesson = lastLesson || api.resumeLesson;
                const redirect = fallbackLesson ? `lessons/${fallbackLesson}` : '';
                window.location.href = `${process.env.NEXT_CONFIG_BASE_PATH || ''}/${redirect}`;
                return;
            }
        }

        // Story previous lesson in history
        if (prevLesson) {
            this.storeHistory(prevLesson, 'end');
        }

        // Prepare state for update
        this.setState(
            {
                initialized: true,
                lesson,
                lessonReady: false,
                ready: false,
            },
            async () => {
                // Get lesson props based on action
                const lessonAction = interacted ? 'load' : 'restore';
                const lessonProps = await this.getLessonActionProps(lesson, lessonAction);

                // Check questions lesson requirement
                if (lessonProps.requireLesson?.length) {
                    return this.props.page.show(lessonProps.requireLesson[0]);
                }

                // Set lesson props
                this.setLessonProps(
                    lessonProps,
                    lessonAction,
                    () => {
                        if (lessonAction === 'load') {
                            this.storeHistory(lesson, 'start');
                        }
                    },
                    interacted
                );
            }
        );
    };

    resetLessonState = async () => {
        if (this.state.lesson) {
            const lessonProps = await this.getLessonActionProps(this.state.lesson, 'restore');
            this.setLessonProps(lessonProps);
        }
    };

    getLessonActionProps = async (lesson: string, lessonAction: GameLessonAction) => {
        // Get page props
        const pageProps = await import(`../../pages/lessons/${lesson}`).then(mod => mod.props).catch(() => ({}));

        // Get lesson props from page and history based on action
        const lessonPageProps = (typeof pageProps === 'function' ? pageProps(this.state) : { ...pageProps }) || {};
        const lessonHistory = (lessonAction === 'restore' && this.getHistory(lesson, 'start')) || null;
        const lessonProps: GameLessonProps = {
            ...lessonPageProps,
            ...lessonHistory,
        };

        // Reset if intro
        if (lesson.length === 1) {
            lessonProps.reset = true;
        }

        // Check history restorations
        lessonProps.history?.restores?.find(restore => {
            const [l, t] = restore.split('-') as any;
            const restoreHistory = this.getHistory(l, t);
            if (restoreHistory) {
                lessonProps.reset = true;
            }
            return restoreHistory;
        });

        return lessonProps;
    };

    setLessonProps = (
        lessonProps: CallableGameLessonProps,
        lessonAction?: GameLessonAction,
        callback?: Callback,
        interacted?: boolean
    ) => {
        // Update state
        this.setState(
            prevState => {
                const { page } = this.props;
                const props = typeof lessonProps === 'function' ? lessonProps(prevState) : lessonProps;

                // Prepare new state
                const newState: GameState = {
                    ...prevState,
                    ...(props.reset ? this.initialState : {}),
                    communityCardsSides: [],
                    holeCardsSide: null,
                    lessonProps: props,
                    lessonReady: true,
                    opponents: props.opponents || (page.lessonParent === 6 ? 4 : 1),
                    opponentsCardsSides: [],
                    startingBudget: page.lessonParent === 2 ? setup.cards.smallBudget : setup.cards.largeBudget,
                };

                // Check history removals
                if (interacted && props.history?.removes) {
                    Object.keys(newState.history).forEach(key => {
                        if (
                            props.history?.removes?.find(
                                removeKey => key === removeKey || key.startsWith(`${removeKey}-`)
                            )
                        ) {
                            delete newState.history[key];
                        }
                    });
                }

                // Apply value props
                Object.entries(props).forEach(([key, value]) => {
                    if (value !== undefined && !['reset'].includes(key)) {
                        (newState as any)[key] = value;
                    }
                });

                // Update budget
                if (props.startingBudget || props.reset) {
                    newState.budget = newState.startingBudget;
                }

                // Update cards and sides
                if (lessonAction !== 'restore') {
                    // Set initial round
                    if (!newState.lesson || newState.lesson.length === 1) {
                        newState.round = 'init';
                    }

                    // Get cards
                    if (props.dealType) {
                        const cards = getCards(newState, props.dealType, newState.opponents);
                        Object.assign(newState, cards);
                    }

                    // Get card sides
                    const cardsSides = getCardsSides(newState.round, newState.opponents, newState.opponentsSpied);
                    newState.communityCardsSides = props.communityCardsSides || cardsSides.communityCardsSides;
                    newState.holeCardsSide =
                        props.holeCardsSide !== undefined ? props.holeCardsSide : cardsSides.holeCardsSide;
                    newState.opponentsCardsSides = props.opponentsCardsSides || cardsSides.opponentsCardsSides;
                }

                return newState;
            },
            async () => {
                // Get dealt cards
                let dealtCards = {};
                const { communityCards, holeCards, opponents, opponentsCards, lessonProps } = this.state;
                if (lessonProps.dealHoleHandNames || lessonProps.dealResultType) {
                    dealtCards = await this.props.api.post<ApiDealRequest, ApiDealResponse>('deal', {
                        communityCards,
                        holeCards,
                        holeHandNames: lessonProps.dealHoleHandNames,
                        opponents,
                        opponentsCards,
                        payableRound: lessonProps.dealPayableRound,
                        resultType: lessonProps.dealResultType,
                    });
                }

                // Complete
                this.setState(
                    {
                        ...dealtCards,
                        lessonReady: true,
                        ready: true,
                    },
                    callback
                );
            }
        );
    };

    getHistory = (lesson: string, type?: GameHistoryType) => {
        // Get by type
        if (type) {
            const key = `${lesson}-${type}`;
            return this.state.history[key] || null;
        }

        // Default to end or start
        const endKey = `${lesson}-end`;
        const startKey = `${lesson}-start`;
        return this.state.history[endKey] || this.state.history[startKey] || null;
    };

    storeHistory = (lesson: string, type: GameHistoryType) => {
        // Add current state to history
        this.setState(
            prevState => {
                // Check store skip
                if (prevState.lessonProps.history?.store === false) {
                    return null;
                }
                return {
                    history: {
                        ...prevState.history,
                        [`${lesson}-${type}`]: {
                            actions: prevState.actions,
                            budget: prevState.budget,
                            communityCards: prevState.communityCards,
                            communityCardsSides: prevState.communityCardsSides,
                            earnings: prevState.earnings,
                            expectedPot: prevState.expectedPot,
                            expectedValue: prevState.expectedValue,
                            hands: prevState.hands,
                            holeCards: prevState.holeCards,
                            holeCardsSide: prevState.holeCardsSide,
                            opponentsCards: prevState.opponentsCards,
                            opponentsCardsSides: prevState.opponentsCardsSides,
                            opponentsSpied: prevState.opponentsSpied,
                            reset: lesson === prevState.lesson && !!prevState.lessonProps.reset,
                            resultType: prevState.resultType,
                            round: prevState.round,
                            winProbability: prevState.winProbability,
                        },
                    },
                };
            },
            () => {
                // Store history in local storage
                const history = JSON.stringify(this.state.history);
                window.localStorage.setItem('sp.game.history', history);
                window.localStorage.setItem('sp.game.history.time', getTime().toString());

                if (type === 'start') {
                    void this.props.api.saveProgress(lesson, this.state.history);
                }

                void this.props.analytics.trackActionEvent('action_game_level_completed', {
                    resource_id: lesson,
                    resource_type: 'step',
                    source: 'game_history',
                });
                if (type === 'end') {
                    void this.props.analytics.trackActionEvent('action_tool_completed', {
                        resource_id: lesson,
                        resource_type: 'tool',
                        source: 'game_history',
                    });
                }
            }
        );
    };

    updateEvaluation = async () => {
        // Set loading
        await new Promise<void>(resolve => {
            this.setState(
                {
                    loading: true,
                },
                resolve
            );
        });

        // Get values
        const { api, page, sounds } = this.props;
        let {
            budget,
            communityCards,
            communityCardsSides,
            currentPot,
            earnings,
            holeCards,
            holeCardsSide,
            lessonProps,
            opponents,
            opponentsCards,
            opponentsCardsSides,
            opponentsSpied,
        } = this.state;

        // Filter cards
        const filteredCommunityCards = communityCards.filter((_, index) => communityCardsSides[index] === 'front');
        const filteredHoleCards = holeCardsSide === 'front' ? holeCards : [];
        const parsedOpponentsCards = opponentsCards.map((opponentCards, index) =>
            opponentsCardsSides[index] === 'front' || opponentsSpied[index] ? opponentCards : null
        );

        // Prepare values
        const folded = lessonProps.resultType === 'fold';
        let holeHandName = null;
        let opponentsHandNames: (GameHandName | null)[] = new Array(opponents).fill(null);
        let resultType: GameResultType | null = null;
        let winningCards = null;
        let winProbability = folded ? 0 : 1 / (opponents + 1);

        // Evaluate
        if (!folded && filteredHoleCards.length) {
            const practice = page.lessonParent === 6;

            // Get data
            const data = await api.post<ApiEvaluateRequest, ApiEvaluateResponse>('evaluate', {
                communityCards: filteredCommunityCards,
                evaluationType: lessonProps.evaluationType,
                holeCards: filteredHoleCards,
                log: practice,
                opponents,
                opponentsCards: parsedOpponentsCards,
            });

            // Update values
            holeHandName = data.holeHandName;
            opponentsHandNames = data.opponentsHandNames;
            resultType = data.resultType;
            winningCards = data.winningCards;
            winProbability = data.winProbability;

            // Check results
            if (resultType && resultType !== this.state.resultType) {
                sounds.play('completed');
                if (resultType === 'win') {
                    budget += practice ? currentPot : 0;
                    earnings += currentPot;
                }
            }
        }

        // Update state
        this.setState({
            budget,
            earnings,
            holeHandName,
            loading: false,
            opponentsHandNames,
            resultType: lessonProps.resultType || resultType,
            winningCards: lessonProps.winningCards || winningCards,
            winProbability,
        });
    };

    debounceUpdateEvaluation = () => {
        clearTimeout(this.updateEvaluationTimeout);
        const callback = this.state.loading ? this.debounceUpdateEvaluation : this.updateEvaluation;
        this.updateEvaluationTimeout = setTimeout(callback, 100);
    };

    updateRound = () => {
        this.setState(prevState => {
            const { content, page, settings } = this.props;
            let {
                budget,
                communityCards,
                communityCardsSides,
                hands,
                holeCards,
                holeCardsSide,
                opponentsCards,
                opponentsCardsSides,
                round,
                winProbability,
            } = prevState;

            // Get pots
            const filteredCommunityCards = communityCards.filter((_, index) => communityCardsSides[index] === 'front');
            const filteredOpponentsCards = opponentsCards.filter((_, index) => opponentsCardsSides[index] === 'front');
            const filteredHoleCards = holeCardsSide === 'front' ? holeCards : [];
            const currentPot = getCurrentPot(filteredCommunityCards);
            const expectedPot = getExpectedPot(filteredHoleCards, filteredCommunityCards, filteredOpponentsCards);

            // Check practice limit
            if (page.lessonParent === 6 && !settings.debugPracticeLimit) {
                const limit = settings.debugPracticeLimit ? Number.POSITIVE_INFINITY : content.game.practiceLimit;
                if (!budget || hands >= limit || (round === 'next' && budget < setup.cards.allInCost)) {
                    round = 'end';
                }
            }

            // Get decision values
            const expectedValue = getExpectedValue(expectedPot, winProbability);
            const roundCost = (setup.cards as any)[`${round}Cost`] || 0;
            const remainingCosts = (setup.cards as any)[`${round}RemainingCosts`] || 0;
            const shouldPay = round === 'hole' || expectedValue > remainingCosts;

            // Update state
            return {
                currentPot,
                expectedPot,
                expectedValue,
                remainingCosts,
                round,
                roundCost,
                shouldPay,
            };
        });
    };

    setHands = (hands: number) => {
        this.setState({
            hands,
        });
    };

    setResultType = (resultType: GameResultType | null) => {
        this.setState({
            resultType,
        });
    };

    setCommunityCards = (communityCards: GameCardType[]) => {
        this.setState({
            communityCards,
        });
    };

    setHoleCards = (holeCards: GameCardType[]) => {
        this.setState({
            holeCards,
        });
    };

    setCardsSides = async (
        community?: GameCardSide | null,
        hole?: GameCardSide | null,
        opponents?: GameCardSide | null
    ) => {
        this.setState(prevState => ({
            communityCardsSides: community ? Array(5).fill(community) : prevState.communityCardsSides,
            holeCardsSide: hole || prevState.holeCardsSide,
            opponentsCardsSides: opponents ? Array(4).fill(opponents) : prevState.opponentsCardsSides,
        }));
        await wait(200);
    };

    shuffleDeal = async (hole?: boolean, opponents?: boolean) => {
        await this.setCardsSides('back', hole ? 'back' : null, opponents ? 'back' : null);

        await new Promise<void>(resolve => {
            const cards = getCards(null, 'all', 1);
            this.setState(cards, resolve);
        });

        await this.setCardsSides('front', hole ? 'front' : null, opponents ? 'front' : null);
    };

    addAction = (type: GameActionType, opponent?: number, intercept?: string, skipDeal?: boolean) => {
        return new Promise<void>(resolve => {
            this.setState(prevState => {
                const { actions, budget, round, shouldPay } = prevState;

                // Get correct
                let correct = type !== 'allIn';
                if (correct && type !== 'spyOpponent') {
                    correct = type === 'fold' ? !shouldPay : shouldPay;
                }

                // Add action
                return {
                    actions: [
                        ...actions,
                        {
                            budget,
                            correct,
                            intercept,
                            opponent,
                            round,
                            skipDeal,
                            type,
                        },
                    ],
                };
            }, resolve);
        });
    };

    continueAction = async () => {
        // Get last action
        const lastAction = _.last(this.state.actions);
        if (!lastAction?.intercept) {
            return;
        }

        // Perform action
        if (lastAction.type === 'allIn') {
            this.allIn(undefined, lastAction.skipDeal);
        } else if (lastAction.type === 'fold') {
            this.fold();
        } else if (lastAction.type !== 'showOpponents' && lastAction.type !== 'spyOpponent') {
            this.pay(lastAction.type, undefined, lastAction.skipDeal);
        }

        // Show lesson page
        this.props.page.show(lastAction.intercept);
    };

    interceptAction = (type: GameActionType, lesson?: string, skipDeal?: boolean) => {
        this.addAction(type, undefined, lesson, skipDeal);
    };

    pay = async (type: GamePayType, progressRound = true, skipDeal?: boolean) => {
        await this.addAction(type);
        this.setState(prevState => {
            const { budget, hands, opponents, opponentsSpied, round } = prevState;

            // Get type props
            const typeProps: Record<string, [GameDealType, number, GameRound, number]> = {
                payHole: ['hole', setup.cards.holeCost, 'flop', 0],
                payFlop: ['flop', setup.cards.flopCost, 'turn', 0],
                payTurn: ['turn', setup.cards.turnCost, 'river', 0],
                payRiver: ['remaining', setup.cards.riverCost, 'next', 1],
            };
            const [dealType, typeCost, typeRound, handIncrease] = typeProps[type];
            const cost = this.props.page.lessonParent === 1 ? 0 : typeCost;
            const nextRound = progressRound ? typeRound : round;
            const cardsSides = progressRound ? getCardsSides(nextRound, opponents, opponentsSpied) : {};

            // Deal cards
            const cards = {};
            if (!skipDeal) {
                const dealtCards = getCards(prevState, dealType, opponents);
                Object.assign(cards, dealtCards);
            }

            // Update state
            return {
                ...cards,
                ...cardsSides,
                budget: budget - cost,
                hands: hands + handIncrease,
                round: nextRound,
            };
        });
    };

    allIn = async (progressRound = true, skipDeal?: boolean) => {
        await this.addAction('allIn');
        this.setState(prevState => {
            const { budget, hands, opponents, opponentsSpied, remainingCosts, round } = prevState;
            const nextRound = progressRound ? 'next' : round;
            const cardsSides = progressRound ? getCardsSides(nextRound, opponents, opponentsSpied) : {};

            // Deal cards
            const cards = {};
            if (!skipDeal) {
                const dealtCards = getCards(prevState, 'remaining', opponents);
                Object.assign(cards, dealtCards);
            }

            // Update state
            return {
                ...cards,
                ...cardsSides,
                budget: budget - remainingCosts,
                hands: hands + 1,
                round: nextRound,
            };
        });
    };

    fold = async (progressRound = true) => {
        await this.addAction('fold');
        this.setState(prevState => ({
            resultType: 'fold',
            hands: prevState.hands + 1,
            round: progressRound ? 'next' : prevState.round,
        }));
    };

    showOpponents = async () => {
        await this.addAction('showOpponents');
        this.setState(prevState => ({
            opponentsCardsSides: new Array(prevState.opponents).fill('front'),
        }));
    };

    spyOpponent = async (index: number) => {
        await this.addAction('spyOpponent', index);
        this.setState(prevState => {
            let { budget, opponents } = prevState;
            budget -= setup.cards.spyOpponentCost;

            const opponentsCardsSides = prevState.opponentsCardsSides.slice();
            opponentsCardsSides[index] = 'front';

            const opponentsSpied = prevState.opponentsSpied.slice();
            opponentsSpied[index] = true;

            return {
                ...getCards(prevState, 'opponent', opponents, index),
                budget,
                opponentsCardsSides,
                opponentsSpied,
            };
        });
    };

    rewind = (revertRound = true) => {
        this.setState(prevState => {
            let { communityCards, holeCards, opponents, opponentsCards, opponentsSpied, round } = prevState;

            // Get last action
            const actions = prevState.actions.slice();
            let action = actions.pop();
            if (!action) {
                return null;
            }

            // Remove previously intercepted
            if (_.last(actions)?.intercept) {
                actions.pop();
            }

            // Update cards
            if (action.round === 'hole') {
                holeCards = [];
            } else if (action.round === 'flop') {
                communityCards = [];
            } else if (action.round === 'turn') {
                communityCards = communityCards.slice(0, 3);
            } else if (action.round === 'river') {
                communityCards = communityCards.slice(0, 4);
                opponentsCards.forEach((_, index) => {
                    if (!opponentsSpied[index]) {
                        delete opponentsCards[index];
                    }
                });
            } else if (action.round === 'opponents') {
                opponentsCards = [];
            }

            // Update opponent spying
            if (action.type === 'spyOpponent' && action.opponent !== undefined) {
                delete opponentsCards[action.opponent];
                delete opponentsSpied[action.opponent];
            }

            // Get round props
            const nextRound = revertRound ? action.round : round;
            const cardsSides = revertRound ? getCardsSides(nextRound, opponents, opponentsSpied) : {};

            // Update state
            return {
                ...cardsSides,
                actions,
                budget: action.budget,
                communityCards,
                holeCards,
                opponentsCards,
                round: nextRound,
            };
        });
    };

    reset = (all?: boolean, revertRound = true) => {
        this.setState(prevState => {
            const { budget, earnings, opponents, opponentsSpied, round, startingBudget } = prevState;
            const nextRound = revertRound ? 'hole' : round;
            const cardsSides = revertRound ? getCardsSides(nextRound, opponents, opponentsSpied) : {};

            return {
                ...cardsSides,
                actions: [],
                budget: all ? startingBudget : budget,
                communityCards: [],
                earnings: all ? 0 : earnings,
                holeCards: [],
                opponentsCards: [],
                opponentsSpied: [],
                round: nextRound,
            };
        });
    };

    render() {
        const { children, page, router } = this.props;
        const { initialized, lesson, lessonReady, ready } = this.state;

        // Parse state
        const state = {
            ...this.state,
            ready: ready && lessonReady && !!page.lesson && router.asPath === `/lessons/${lesson}`,
        };

        return <GameContext.Provider value={state}>{initialized && children}</GameContext.Provider>;
    }
}

export const GameProvider = compose(
    withAnalytics,
    withApi,
    withContent,
    withPage,
    withRouter,
    withSettings,
    withSounds
)(Game);
